// Ported from packages/desk/src/mobile/Sheet.tsx (docs/PLAN-2026-09-21-mobile-os-shell.md, step 4). Same file name and props
// as the desk copy, the dk- prefix renamed rdr-, so a fix in one is a copy in the other. Radar never imports @gamo/desk.
/**
 * The bottom sheet every phone layer is drawn in (the App Library, Search, Profile): it rises over the home screen, is
 * a labelled modal dialog, takes focus and keeps it, and closes by a swipe down on its handle, by Escape, by the
 * browser's Back, or by a tap on the dimmed screen above it.
 */
import { useRef, useState, type ReactNode } from "react";
import { useBackToClose, useFocusTrap } from "./layers";

/** How far the handle must be pulled down before letting go closes the sheet. */
export const SWIPE_CLOSE_PX = 90;

export function Sheet({
  open,
  onClose,
  title,
  titleId,
  children,
  className,
  header,
  data,
}: {
  open: boolean;
  onClose: () => void;
  /** The sheet's name for a screen reader, and its heading. */
  title: string;
  titleId: string;
  children: ReactNode;
  className?: string;
  /** Beside the heading, for example a search field. */
  header?: ReactNode;
  /** A `data-rdr-*` hook for tours and tests. */
  data?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState(0);
  const start = useRef<{ y: number; id: number } | null>(null);
  useBackToClose(open, onClose);
  useFocusTrap(ref, open, onClose);
  if (!open) return null;

  const dataAttr = data ? { [`data-rdr-${data}`]: "" } : {};
  return (
    <div className="rdr-mlayer" onClick={onClose}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={"rdr-msheet" + (className ? ` ${className}` : "") + (drag > 0 ? " dragging" : "")}
        style={drag > 0 ? { transform: `translateY(${drag}px)` } : undefined}
        onClick={(e) => e.stopPropagation()}
        {...dataAttr}
      >
        <div
          className="rdr-msheet-grab"
          onPointerDown={(e) => {
            // A field in the header is for typing, not for dragging.
            if ((e.target as Element).closest("input, textarea, select")) return;
            start.current = { y: e.clientY, id: e.pointerId };
          }}
          onPointerMove={(e) => {
            if (start.current?.id !== e.pointerId) return;
            const dy = Math.max(0, e.clientY - start.current.y);
            // Captured only once it is clearly a drag, so a plain tap on the handle stays a click.
            if (dy > 6 && !e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.setPointerCapture?.(e.pointerId);
            setDrag(dy);
          }}
          onPointerUp={(e) => {
            const s = start.current;
            start.current = null;
            if (!s) return;
            const pulled = e.clientY - s.y;
            setDrag(0);
            if (pulled >= SWIPE_CLOSE_PX) onClose();
          }}
          onPointerCancel={() => {
            start.current = null;
            setDrag(0);
          }}
        >
          <button type="button" className="rdr-msheet-handle" aria-label={`Close ${title}`} onClick={onClose}>
            <span aria-hidden="true" />
          </button>
          <div className="rdr-msheet-head">
            <h2 id={titleId} className="rdr-msheet-title">
              {title}
            </h2>
            {header}
          </div>
        </div>
        <div className="rdr-msheet-body">{children}</div>
      </div>
    </div>
  );
}
